import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders } from '../lib/useQobuz';

const GOLD = '#C9A84C';
const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%231a1a2e" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23333" font-size="48"%3E♪%3C/text%3E%3C/svg%3E';

function ScrollRow({ title, children, isEmpty, grid = false }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll, children]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: 'smooth' });
  };

  if (isEmpty) return null;

  if (grid) {
    return (
      <div className="mb-8">
        <h3
          className="text-base font-semibold tracking-wide mb-3 px-1"
          style={{ color: 'var(--text-primary, #fff)' }}
        >
          {title}
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '1rem',
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h3
        className="text-base font-semibold tracking-wide mb-3 px-1"
        style={{ color: 'var(--text-primary, #fff)' }}
      >
        {title}
      </h3>
      <div className="relative group">
        {canScrollLeft && (
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-0 bottom-0 z-10 w-10 items-center justify-center opacity-0 pointer-fine:group-hover:opacity-100 transition-opacity hidden pointer-fine:flex"
            style={{ background: 'linear-gradient(to right, rgba(10,10,20,0.95), transparent)' }}
            aria-label="Scroll left"
          >
            <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide"
          style={{
            scrollSnapType: 'x proximity',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {children}
        </div>
        {canScrollRight && (
          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-0 bottom-0 z-10 w-10 items-center justify-center opacity-0 pointer-fine:group-hover:opacity-100 transition-opacity hidden pointer-fine:flex"
            style={{ background: 'linear-gradient(to left, rgba(10,10,20,0.95), transparent)' }}
            aria-label="Scroll right"
          >
            <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function AlbumCard({ album, onClick }) {
  const year = album.releaseDate ? new Date(album.releaseDate).getFullYear() : '';
  return (
    <button
      type="button"
      onClick={() => onClick(album)}
      className="flex-shrink-0 text-left group/card transition-transform hover:scale-[1.02] touch-manipulation select-none [-webkit-tap-highlight-color:transparent]"
      style={{ width: 160, scrollSnapAlign: 'start' }}
    >
      <div className="relative w-[160px] h-[160px] rounded-lg overflow-hidden mb-2 shadow-lg">
        <img
          src={album.coverUrl || album.coverUrlSmall || PLACEHOLDER}
          alt={album.title}
          className="w-full h-full object-cover pointer-events-none"
          loading="lazy"
          draggable={false}
          onError={(e) => { e.target.src = PLACEHOLDER; }}
        />
        <div
          className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity flex items-center justify-center pointer-events-none"
          style={{ background: 'rgba(0,0,0,0.4)' }}
        >
          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      <p
        className="text-sm font-medium truncate"
        style={{ color: 'var(--text-primary, #fff)' }}
        title={album.title}
      >
        {album.title}
      </p>
      <p
        className="text-xs truncate"
        style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}
      >
        {year}{album.artistName && year ? ' · ' : ''}{album.artistName || ''}
      </p>
    </button>
  );
}

function ArtistCard({ artist, onClick }) {
  return (
    <button
      onClick={() => onClick(artist)}
      className="flex-shrink-0 text-center group/card transition-transform hover:scale-[1.02]"
      style={{ width: 140, scrollSnapAlign: 'start' }}
    >
      <div className="relative w-[120px] h-[120px] mx-auto rounded-full overflow-hidden mb-2 shadow-lg">
        <img
          src={artist.imageUrl || PLACEHOLDER}
          alt={artist.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.target.src = PLACEHOLDER; }}
        />
        <div
          className="absolute inset-0 rounded-full opacity-0 group-hover/card:opacity-100 transition-opacity"
          style={{ background: 'rgba(0,0,0,0.3)' }}
        />
      </div>
      <p
        className="text-sm font-medium truncate"
        style={{ color: 'var(--text-primary, #fff)' }}
        title={artist.name}
      >
        {artist.name}
      </p>
      <p
        className="text-xs"
        style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}
      >
        Artist
      </p>
    </button>
  );
}

function CreditCard({ person, onClick }) {
  const isNavigable = !!person.artistId;
  const Tag = isNavigable ? 'button' : 'div';
  return (
    <Tag
      {...(isNavigable ? { onClick: () => onClick(person) } : {})}
      className={`flex-shrink-0 text-center group/card transition-transform ${isNavigable ? 'cursor-pointer hover:scale-[1.02]' : 'cursor-default'}`}
      style={{ width: 130, scrollSnapAlign: 'start' }}
    >
      <div className="relative w-[100px] h-[100px] mx-auto rounded-full overflow-hidden mb-2 shadow-lg" style={{ background: 'var(--bg-card, #1e1e30)' }}>
        {person.imageUrl ? (
          <img
            src={person.imageUrl}
            alt={person.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.src = PLACEHOLDER; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-3xl" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.3))' }}>
              {person.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
        )}
      </div>
      <p
        className="text-sm font-medium truncate"
        style={{ color: isNavigable ? 'var(--text-primary, #fff)' : 'var(--text-secondary, rgba(255,255,255,0.6))' }}
        title={person.name}
      >
        {person.name}
      </p>
      {person.roles?.length > 0 && (
        <p
          className="text-xs truncate"
          style={{ color: GOLD }}
          title={person.roles.join(', ')}
        >
          {person.roles.slice(0, 2).join(', ')}
        </p>
      )}
    </Tag>
  );
}

function SkeletonRow() {
  return (
    <div className="mb-8">
      <div className="h-5 w-48 rounded bg-white/5 mb-3 animate-pulse" />
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex-shrink-0" style={{ width: 160 }}>
            <div className="w-[160px] h-[160px] rounded-lg bg-white/5 animate-pulse mb-2" />
            <div className="h-4 w-32 rounded bg-white/5 animate-pulse mb-1" />
            <div className="h-3 w-20 rounded bg-white/5 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AlbumDiscoveryRows({ albumId, artistName, onAlbumClick, onArtistClick, onPersonClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(null);

  useEffect(() => {
    if (!albumId) return;
    if (fetchedRef.current === albumId) return;
    fetchedRef.current = albumId;

    const serviceAlbumId = String(albumId).replace(/^qobuz:album:/, '');
    setLoading(true);
    setData(null);

    fetch(`${CLOUD_API_URL}/api/streaming/qobuz/album-related?album_id=${encodeURIComponent(serviceAlbumId)}`, {
      headers: getHeaders(),
    })
      .then(r => r.ok ? r.json() : null)
      .then(result => {
        if (result) setData(result);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [albumId]);

  const handleAlbumClick = useCallback((album) => {
    if (onAlbumClick) onAlbumClick(album);
  }, [onAlbumClick]);

  const handleArtistClick = useCallback((artist) => {
    if (onArtistClick) onArtistClick(artist.serviceArtistId || artist.id, artist.name);
  }, [onArtistClick]);

  const handlePersonClick = useCallback((person) => {
    if (!person.artistId) return;
    const qobuzId = person.artistId.replace(/^qobuz:artist:/, '');
    if (onArtistClick) {
      onArtistClick(qobuzId, person.name);
    } else if (onPersonClick) {
      onPersonClick(qobuzId, person.name);
    }
  }, [onPersonClick, onArtistClick]);

  if (loading) {
    return (
      <div className="px-6 pt-6">
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (!data) return null;

  const { discography, similarAlbums, similarArtists, credits } = data;
  const displayArtistName = data.artistName || artistName || '';
  const hasDiscography = discography?.length > 0;
  const hasSimilarAlbums = similarAlbums?.length > 0;
  const hasSimilarArtists = similarArtists?.length > 0;
  const hasCredits = credits?.length > 0;

  if (!hasDiscography && !hasSimilarAlbums && !hasSimilarArtists && !hasCredits) return null;

  // The last visible section renders as a permanent CSS grid (no scroll, no toggle).
  const lastIsCredits = hasCredits;
  const lastIsSimilarArtists = !hasCredits && hasSimilarArtists;
  const lastIsSimilarAlbums = !hasCredits && !hasSimilarArtists && hasSimilarAlbums;

  return (
    <div className="px-6 pt-4 pb-2">
      <ScrollRow
        title={`More by ${displayArtistName}`}
        isEmpty={!hasDiscography}
        grid={!hasSimilarAlbums && !hasSimilarArtists && !hasCredits}
      >
        {(discography || []).map((album) => (
          <AlbumCard key={album.id || album.serviceAlbumId} album={album} onClick={handleAlbumClick} />
        ))}
      </ScrollRow>

      <ScrollRow
        title="Albums You Might Love"
        isEmpty={!hasSimilarAlbums}
        grid={lastIsSimilarAlbums}
      >
        {(similarAlbums || []).map((album) => (
          <AlbumCard key={album.id || album.serviceAlbumId} album={album} onClick={handleAlbumClick} />
        ))}
      </ScrollRow>

      <ScrollRow
        title="If You Like This, Try"
        isEmpty={!hasSimilarArtists}
        grid={lastIsSimilarArtists}
      >
        {(similarArtists || []).map((artist) => (
          <ArtistCard key={artist.id || artist.serviceArtistId} artist={artist} onClick={handleArtistClick} />
        ))}
      </ScrollRow>

      <ScrollRow
        title="The Players"
        isEmpty={!hasCredits}
        grid={lastIsCredits}
      >
        {(credits || []).map((person, i) => (
          <CreditCard key={`${person.name}-${i}`} person={person} onClick={handlePersonClick} />
        ))}
      </ScrollRow>
    </div>
  );
}
